<template>
  <b-form class="page-section" @submit.prevent="onSubmit">
    <b-form-group
      label="Personality Type"
      label-for="personality_type"
      label-class="form-label"
      :description="isSchoolScenario ? 'Not used for scenarios. Describe the character in the patient brief.' : ''"
    >
      <div class="row px-3">
        <div v-for="option in personalityOptions" :key="option" class="mr-3">
          <div class="d-flex align-items-center">
            <b-form-radio v-model="simulation.personality" :value="option">
              <div class="mr-1">{{ option }}</div>
            </b-form-radio>
          </div>
        </div>
      </div>

      <b-form-invalid-feedback
        :state="!$v.simulation.personality.required && $v.simulation.personality.$dirty ? false : null"
        >This field is required.</b-form-invalid-feedback
      >
    </b-form-group>

    <b-form-group v-if="isAnam" label="Choose Voice" label-for="anam_voice" label-class="form-label">
      <div v-if="loadingVoices" class="text-muted">
        <i class="fas fa-circle-notch fa-spin mr-1"></i> Loading Anam voices...
      </div>
      <b-alert v-else-if="voicesError" show variant="danger" class="mb-2">
        {{ voicesError }}
      </b-alert>
      <div v-else class="row px-3">
        <div v-for="option in anamVoices" :key="option.id" class="mr-3">
          <div class="d-flex align-items-center">
            <b-form-radio v-model="simulation.voice" :value="option.id">
              <div class="mr-1">{{ option.name }}</div>
            </b-form-radio>
            <md-icon
              v-if="option.sample_url"
              class="clickable-item"
              @click.native="playSound(option.sample_url)"
              >volume_up</md-icon
            >
          </div>
        </div>
      </div>

      <b-form-invalid-feedback
        :state="!$v.simulation.voice.required && $v.simulation.voice.$dirty ? false : null"
        >This field is required.</b-form-invalid-feedback
      >
    </b-form-group>

    <b-form-group v-if="isAnam" label="Choose Avatar" label-for="anam_avatar" label-class="form-label">
      <div v-if="loadingAvatars" class="text-muted">
        <i class="fas fa-circle-notch fa-spin mr-1"></i> Loading Anam avatars...
      </div>
      <b-alert v-else-if="avatarsError" show variant="danger" class="mb-2">
        {{ avatarsError }}
      </b-alert>
      <div v-else class="d-flex flex-wrap px-3">
        <div
          v-for="option in visibleAvatars"
          :key="option.id"
          class="avatar-option text-center mr-3 mb-3 clickable-item"
          :class="{ 'avatar-option--selected': simulation.avatar === option.id }"
          @click="simulation.avatar = option.id"
        >
          <img v-if="option.image_url" class="avatar-option__image" :src="option.image_url" :alt="option.name" />
          <div class="avatar-option__name">{{ option.name }}</div>
        </div>
      </div>
      <div v-if="!loadingAvatars && !avatarsError && avatarLimit < anamAvatars.length" class="px-3">
        <b-btn variant="link" class="p-0" @click="avatarLimit += AVATAR_PAGE_STEP"
          >Load more avatars ({{ anamAvatars.length - avatarLimit }} left)</b-btn
        >
      </div>
    </b-form-group>

    <b-form-group v-else label="Choose Voice" label-for="choose_voice" label-class="form-label">
      <div class="row px-3">
        <div v-for="option in SIMULATION_VOICES_OPTIONS" :key="option.value" class="mr-3">
          <div class="d-flex align-items-center">
            <b-form-radio v-model="simulation.voice" :value="option.value">
              <div class="d-flex align-items-center">
                <img class="mr-1" style="width: 25px; height: 25px" :src="getImage(option.value)" alt="" />
                <div class="mr-1">{{ option.name }}</div>
              </div>
            </b-form-radio>
            <md-icon class="clickable-item" @click.native="playSound(option.voice)">volume_up</md-icon>
          </div>
        </div>
      </div>

      <b-form-invalid-feedback :state="!$v.simulation.voice.required && $v.simulation.voice.$dirty ? false : null"
        >This field is required.</b-form-invalid-feedback
      >
    </b-form-group>

    <!-- Anam only, and super admin only: the model that conducts this
         interview. Leaving it on the environment default is what every
         interview authored before this picker existed still runs. -->
    <b-form-group
      v-if="isAnam && isSuperAdmin"
      label="Interview Model"
      label-for="anam_llm"
      label-class="form-label"
    >
      <div v-if="loadingLlms" class="text-muted">
        <i class="fas fa-circle-notch fa-spin mr-1"></i> Loading Anam models...
      </div>
      <b-alert v-else-if="llmsError" show variant="danger" class="mb-2">
        {{ llmsError }}
      </b-alert>
      <b-form-select v-else id="anam_llm" v-model="simulation.llmId" :options="llmOptions" />
      <b-form-text
        >Anam does not report which models can call tools, and the interview ends itself by
        calling them. Run one interview end to end on a model before using it for
        real.</b-form-text
      >
    </b-form-group>

    <b-form-group label="Greetings" label-for="greetings" label-class="form-label">
      <b-form-input
        id="greetings"
        placeholder="Enter greetings."
        v-model="simulation.greetings"
        required
        :state="!$v.simulation.greetings.required && $v.simulation.greetings.$dirty ? false : null"
      />
      <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
    </b-form-group>

    <b-form-group label="Closing" label-for="closing" label-class="form-label">
      <b-form-input
        id="closing"
        placeholder="Enter closing."
        v-model="simulation.closing"
        required
        :state="!$v.simulation.closing.required && $v.simulation.closing.$dirty ? false : null"
      />
      <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
    </b-form-group>
  </b-form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { get, pick } from 'lodash';

import { ANAM_DEFAULT_AVATAR_ID, SIMULATION_VOICES_OPTIONS, USER_ROLES } from '../../../common/constants';

// Avatars revealed per click of "Load more".
const AVATAR_PAGE_STEP = 5;

export default {
  name: 'JobQualificationsForm',
  components: {},

  props: {
    simulationData: { type: Object, required: true }
  },

  data() {
    return {
      simulation: {
        voice: '',
        closing: '',
        greetings: '',
        personality: '',
        provider: 'retell',
        avatar: ANAM_DEFAULT_AVATAR_ID,
        llmId: ''
      },
      SIMULATION_VOICES_OPTIONS,
      anamVoices: [],
      loadingVoices: false,
      voicesError: '',
      anamAvatars: [],
      loadingAvatars: false,
      avatarsError: '',
      avatarLimit: AVATAR_PAGE_STEP,
      AVATAR_PAGE_STEP,
      anamLlms: [],
      loadingLlms: false,
      llmsError: '',
      personalityOptions: ['Standard', 'Casual/Conversational', 'Challenging'],
      audioPlaying: false,
      audioFile: new Audio()
    };
  },

  validations() {
    return {
      simulation: {
        voice: { required },
        closing: { required },
        greetings: { required },
        personality: { required }
      }
    };
  },

  computed: {
    isSchoolScenario() {
      return this.simulationData.simulation_purpose === 'school_scenario';
    },
    ...mapGetters('auth', ['getLoggedInUser']),

    // The backend only reads this field for a super admin, so anyone else is
    // shown a control that would be ignored.
    isSuperAdmin() {
      return get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN;
    },

    isAnam() {
      return this.simulation.provider === 'anam';
    },

    // Revealed a few at a time, in Anam's own order. The order must never
    // depend on the selection, or picking a face would reshuffle the grid
    // under the author's cursor.
    visibleAvatars() {
      return this.anamAvatars.slice(0, this.avatarLimit);
    },

    // The blank option is a real choice, not a placeholder: it leaves the
    // interview on whatever the environment default is, rather than pinning it
    // to whichever model happens to be default today.
    llmOptions() {
      return [
        { value: '', text: 'Environment default' },
        ...this.anamLlms.map(llm => ({ value: llm.id, text: this.llmLabel(llm) }))
      ];
    }
  },

  watch: {
    isAnam: {
      handler(value) {
        if (value) {
          this.fetchAnamVoices();
          this.fetchAnamAvatars();
          if (this.isSuperAdmin) this.fetchAnamLlms();
        }
      },
      immediate: true
    },
    // The two providers have disjoint voice catalogues -- carrying a Retell
    // voice id over to Anam is rejected with "Voice not found".
    'simulation.provider'(value, previous) {
      if (previous && value !== previous) this.simulation.voice = '';
    },
    'simulation.avatar'() {
      if (this.anamAvatars.length) this.revealSelectedAvatar();
    },
    simulationData: {
      handler() {
        this.simulation = pick(this.simulationData, [
          'voice',
          'closing',
          'greetings',
          'personality',
          'provider',
          'avatar',
          'llmId'
        ]);
      },
      deep: true
    }
  },

  methods: {
    ...mapActions('simulations', ['getAnamVoices', 'getAnamAvatars', 'getAnamLlms']),

    // Anam voice ids are UUIDs from Anam's own catalogue -- a Retell voice
    // name like 'openai-Nova' is rejected with "Voice not found", so the
    // author picks from the real list rather than typing an id.
    async fetchAnamVoices() {
      if (this.anamVoices.length || this.loadingVoices) return;
      this.loadingVoices = true;
      this.voicesError = '';
      try {
        const res = await this.getAnamVoices();
        this.anamVoices = get(res, 'data', []);
      } catch (e) {
        this.voicesError = get(e, 'response.data.message', 'Could not load Anam voices.');
      }
      this.loadingVoices = false;
    },

    // The avatar is the face the student sees. Anam's ids are UUIDs from its
    // own gallery, so the author picks a thumbnail rather than typing one.
    async fetchAnamAvatars() {
      if (this.anamAvatars.length || this.loadingAvatars) return;
      this.loadingAvatars = true;
      this.avatarsError = '';
      try {
        const res = await this.getAnamAvatars();
        this.anamAvatars = get(res, 'data', []);
        this.revealSelectedAvatar();
      } catch (e) {
        this.avatarsError = get(e, 'response.data.message', 'Could not load Anam avatars.');
      }
      this.loadingAvatars = false;
    },

    // Anam reports no tool-calling capability, so the list is shown as-is and
    // the warning under the select carries the risk instead.
    async fetchAnamLlms() {
      if (this.anamLlms.length || this.loadingLlms) return;
      this.loadingLlms = true;
      this.llmsError = '';
      try {
        const res = await this.getAnamLlms();
        this.anamLlms = get(res, 'data', []);
      } catch (e) {
        this.llmsError = get(e, 'response.data.message', 'Could not load Anam models.');
      }
      this.loadingLlms = false;
    },

    // Only ownership is worth marking. Anam reports every model it supplies as
    // a "default", so labelling those would put the word beside almost every
    // row and tell the author nothing.
    llmLabel(llm) {
      return llm.is_custom ? `${llm.name} (custom)` : llm.name;
    },

    // Editing an interview whose avatar sits past the first page would show
    // nothing selected, so enough pages are opened to reach it.
    revealSelectedAvatar() {
      const index = this.anamAvatars.findIndex(a => a.id === this.simulation.avatar);
      if (index < this.avatarLimit) return;
      this.avatarLimit = Math.ceil((index + 1) / AVATAR_PAGE_STEP) * AVATAR_PAGE_STEP;
    },

    validateFormData() {
      this.$v.$touch();
      return {
        isValid: !this.$v.$invalid,
        formData: { ...this.simulation }
      };
    },
    playSound(sound) {
      if (this.audioPlaying) {
        this.audioFile.pause();
        this.audioPlaying = false;
      }

      this.audioFile = new Audio(sound);
      this.audioFile.play();
      this.audioPlaying = true;
    },
    getImage(voice) {
      if (voice) {
        return require(`@/assets/images/avatars/${
          this.SIMULATION_VOICES_OPTIONS.find(item => item.value === voice).image
        }`);
      }
    }
  },

  async mounted() {}
};
</script>

<style lang="scss" scoped>
.avatar-option {
  width: 96px;

  &__image {
    width: 96px;
    height: 96px;
    object-fit: cover;
    border-radius: 8px;
    border: 3px solid transparent;
  }

  &__name {
    font-size: 12px;
    line-height: 1.2;
    margin-top: 4px;
  }

  &--selected .avatar-option__image {
    border-color: #00c58e;
  }
}
</style>
